import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Mail, Search, Zap, CheckCircle, AlertCircle, Trash2, 
  RefreshCw, Send, FileText, Users, Target, ChevronDown, ChevronUp,
  Building2, Star, Clock, Link2, Unlink, Inbox, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { api } from '../utils/api';

export default function EmailParser() {
  const [searchParams] = useSearchParams();
  const [emailContent, setEmailContent] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [isParsingEmail, setIsParsingEmail] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [matches, setMatches] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(true);
  const [isLoadingMatches, setIsLoadingMatches] = useState(true);
  const [isFindingMatches, setIsFindingMatches] = useState(false);
  const [activeTab, setActiveTab] = useState('gmail'); // gmail, parse, programs, matches
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [dailySummary, setDailySummary] = useState(null);
  
  // Gmail state
  const [gmailStatus, setGmailStatus] = useState({ is_configured: false, is_connected: false });
  const [isConnectingGmail, setIsConnectingGmail] = useState(false);
  const [gmailEmails, setGmailEmails] = useState([]);
  const [isFetchingEmails, setIsFetchingEmails] = useState(false);
  const [gmailFilters, setGmailFilters] = useState({ sender: '', subject: '', max_results: 20, auto_parse: false });

  useEffect(() => {
    fetchPrograms();
    fetchMatches();
    fetchLeads();
    fetchDailySummary();
    fetchGmailStatus();
    
    // Check if redirected from Gmail OAuth
    if (searchParams.get('gmail') === 'connected') {
      toast.success('Gmail connected successfully!');
      fetchGmailStatus();
    }
  }, [searchParams]);

  const fetchGmailStatus = async () => {
    try {
      const response = await api.get('/gmail/status');
      setGmailStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Gmail status:', error);
    }
  };

  const fetchPrograms = async () => {
    try {
      setIsLoadingPrograms(true);
      const response = await api.get('/email-parser/programs');
      setPrograms(response.data.programs || []);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
    } finally {
      setIsLoadingPrograms(false);
    }
  };

  const fetchMatches = async () => {
    try {
      setIsLoadingMatches(true);
      const response = await api.get('/lead-matching/matches');
      setMatches(response.data.matches || []);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    } finally {
      setIsLoadingMatches(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await api.get('/leads');
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  };

  const fetchDailySummary = async () => {
    try {
      const response = await api.get('/lead-matching/daily-summary');
      setDailySummary(response.data);
    } catch (error) {
      console.error('Failed to fetch daily summary:', error);
    }
  };

  const handleParseEmail = async () => {
    if (!emailContent.trim()) {
      toast.error('Please paste email content first');
      return;
    }

    setIsParsingEmail(true);
    try {
      const response = await api.post('/email-parser/parse', {
        email_content: emailContent,
        email_subject: emailSubject,
        sender_email: senderEmail
      });

      if (response.data.success) {
        const lendersCreated = response.data.lenders_created || [];
        if (lendersCreated.length > 0) {
          toast.success(`New lender created: ${lendersCreated.map(l => l.lender_name).join(', ')}`, { duration: 5000 });
          if (response.data.notification_sent) {
            toast.info('📧 Notification email sent to admin', { duration: 3000 });
          }
        }
        toast.success(`Extracted ${response.data.programs_extracted} program(s) from email`);
        setEmailContent('');
        setEmailSubject('');
        setSenderEmail('');
        fetchPrograms();
      } else {
        toast.error(response.data.error || 'Failed to parse email');
      }
    } catch (error) {
      toast.error('Failed to parse email');
      console.error(error);
    } finally {
      setIsParsingEmail(false);
    }
  };

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm('Delete this program?')) return;

    try {
      await api.delete(`/email-parser/programs/${programId}`);
      toast.success('Program deleted');
      fetchPrograms();
      fetchMatches();
    } catch (error) {
      toast.error('Failed to delete program');
    }
  };

  // Gmail functions
  const handleConnectGmail = async () => {
    setIsConnectingGmail(true);
    try {
      const response = await api.get('/gmail/auth-url');
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Gmail connection');
      setIsConnectingGmail(false);
    }
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm('Disconnect Gmail? You will need to reconnect to fetch emails.')) return;
    
    try {
      await api.delete('/gmail/disconnect');
      toast.success('Gmail disconnected');
      setGmailStatus({ ...gmailStatus, is_connected: false, gmail_email: null });
      setGmailEmails([]);
    } catch (error) {
      toast.error('Failed to disconnect Gmail');
    }
  };

  const handleFetchGmailEmails = async () => {
    setIsFetchingEmails(true);
    try {
      const response = await api.post('/gmail/fetch-emails', gmailFilters);
      setGmailEmails(response.data.emails || []);
      toast.success(response.data.message);
      
      if (gmailFilters.auto_parse && response.data.parsed_count > 0) {
        fetchPrograms();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch emails');
    } finally {
      setIsFetchingEmails(false);
    }
  };

  const handleParseGmailEmail = async (gmailId) => {
    try {
      const response = await api.post(`/gmail/parse-email/${gmailId}`);
      if (response.data.success) {
        toast.success(`Extracted ${response.data.programs_extracted} program(s)`);
        fetchPrograms();
        // Update email in list to show it's been parsed
        setGmailEmails(prev => prev.map(e => 
          e.gmail_id === gmailId ? { ...e, is_unread: false, parsed: true } : e
        ));
      } else {
        toast.error(response.data.error || 'Failed to parse email');
      }
    } catch (error) {
      toast.error('Failed to parse email');
    }
  };

  const handleFindMatches = async (leadId) => {
    setIsFindingMatches(true);
    try {
      const response = await api.post(`/lead-matching/find-matches/${leadId}`);
      if (response.data.success) {
        toast.success(`Found ${response.data.matches_found} matching programs for ${response.data.lead_name}`);
        fetchMatches();
      } else {
        toast.error(response.data.error || 'Failed to find matches');
      }
    } catch (error) {
      toast.error('Failed to find matches');
    } finally {
      setIsFindingMatches(false);
    }
  };

  const handleUpdateMatchStatus = async (matchId, status) => {
    try {
      await api.patch(`/lead-matching/matches/${matchId}?status=${status}`);
      toast.success(`Match marked as ${status}`);
      fetchMatches();
    } catch (error) {
      toast.error('Failed to update match status');
    }
  };

  const handleSendDailySummary = async () => {
    try {
      const response = await api.post('/lead-matching/send-daily-email');
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to send daily summary');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400 bg-green-400/10';
    if (score >= 60) return 'text-yellow-400 bg-yellow-400/10';
    return 'text-red-400 bg-red-400/10';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500/20 text-blue-400';
      case 'reviewed': return 'bg-yellow-500/20 text-yellow-400';
      case 'contacted': return 'bg-green-500/20 text-green-400';
      case 'dismissed': return 'bg-gray-500/20 text-gray-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="email-parser-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Zap className="w-7 h-7 text-gold" />
            Email Parser & Lead Matching
          </h1>
          <p className="text-slate-400 mt-1">
            Extract lender programs from emails and automatically match them to your leads
          </p>
        </div>
        <Button 
          onClick={handleSendDailySummary}
          className="bg-gold hover:bg-gold-light text-slate-950"
          data-testid="send-summary-btn"
        >
          <Send className="w-4 h-4 mr-2" />
          Send Daily Summary
        </Button>
      </div>

      {/* Daily Summary Card */}
      {dailySummary && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gold" />
              <span className="text-slate-300">
                <strong className="text-white">{dailySummary.new_programs_count}</strong> new programs today
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-green-400" />
              <span className="text-slate-300">
                <strong className="text-white">{dailySummary.new_matches_count}</strong> new matches found
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2 overflow-x-auto">
        {[
          { id: 'gmail', label: gmailStatus.is_connected ? 'Gmail Connected' : 'Connect Gmail', icon: Inbox },
          { id: 'parse', label: 'Paste Email', icon: Mail },
          { id: 'programs', label: `Programs (${programs.length})`, icon: FileText },
          { id: 'matches', label: `Matches (${matches.length})`, icon: Target }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === tab.id 
                ? 'bg-gold text-slate-950 font-semibold' 
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            } ${tab.id === 'gmail' && gmailStatus.is_connected ? 'border border-green-500/50' : ''}`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Gmail Tab */}
      {activeTab === 'gmail' && (
        <div className="space-y-6">
          {/* Gmail Connection Status */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Inbox className="w-5 h-5 text-gold" />
              Gmail Integration
            </h2>
            
            {!gmailStatus.is_configured ? (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-400 font-medium mb-2">Gmail API Not Configured</p>
                <p className="text-slate-400 text-sm">
                  To enable automatic email reading, add these environment variables to your backend:
                </p>
                <ul className="text-slate-500 text-sm mt-2 list-disc list-inside">
                  <li>GMAIL_API_CLIENT_ID</li>
                  <li>GMAIL_API_CLIENT_SECRET</li>
                  <li>GMAIL_API_REDIRECT_URI</li>
                </ul>
                <p className="text-slate-400 text-sm mt-2">
                  You can still use the "Paste Email" tab to manually extract programs.
                </p>
              </div>
            ) : gmailStatus.is_connected ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-400" />
                    <div>
                      <p className="text-green-400 font-medium">Gmail Connected</p>
                      <p className="text-slate-400 text-sm">{gmailStatus.gmail_email}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDisconnectGmail}
                    className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                    data-testid="disconnect-gmail-btn"
                  >
                    <Unlink className="w-4 h-4 mr-2" />
                    Disconnect
                  </Button>
                </div>
                
                {/* Email Filters */}
                <div className="bg-slate-900/50 rounded-lg p-4 space-y-4">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gold" />
                    Filter Emails
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-slate-400 mb-1 block">From (Sender)</label>
                      <input
                        type="text"
                        value={gmailFilters.sender}
                        onChange={(e) => setGmailFilters({...gmailFilters, sender: e.target.value})}
                        placeholder="e.g., programs@lender.com"
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder:text-slate-500 focus:border-gold outline-none"
                        data-testid="gmail-filter-sender"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-slate-400 mb-1 block">Subject Contains</label>
                      <input
                        type="text"
                        value={gmailFilters.subject}
                        onChange={(e) => setGmailFilters({...gmailFilters, subject: e.target.value})}
                        placeholder="e.g., program, rate sheet"
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder:text-slate-500 focus:border-gold outline-none"
                        data-testid="gmail-filter-subject"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={gmailFilters.auto_parse}
                        onChange={(e) => setGmailFilters({...gmailFilters, auto_parse: e.target.checked})}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-gold focus:ring-gold"
                      />
                      <span className="text-slate-300 text-sm">Auto-parse all fetched emails</span>
                    </label>
                    <select
                      value={gmailFilters.max_results}
                      onChange={(e) => setGmailFilters({...gmailFilters, max_results: parseInt(e.target.value)})}
                      className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-gold outline-none"
                    >
                      <option value={10}>10 emails</option>
                      <option value={20}>20 emails</option>
                      <option value={50}>50 emails</option>
                    </select>
                  </div>
                  <Button
                    onClick={handleFetchGmailEmails}
                    disabled={isFetchingEmails}
                    className="w-full bg-gold hover:bg-gold-light text-slate-950 font-semibold py-3"
                    data-testid="fetch-gmail-btn"
                  >
                    {isFetchingEmails ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Fetching Emails...
                      </>
                    ) : (
                      <>
                        <Inbox className="w-4 h-4 mr-2" />
                        Fetch Emails from Gmail
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Email List */}
                {gmailEmails.length > 0 && (
                  <div className="space-y-3 mt-4">
                    <h3 className="text-white font-medium">Fetched Emails ({gmailEmails.length})</h3>
                    <div className="max-h-96 overflow-y-auto space-y-2">
                      {gmailEmails.map((email) => (
                        <div 
                          key={email.gmail_id}
                          className={`bg-slate-900/50 border rounded-lg p-4 ${
                            email.parsed ? 'border-green-500/30' : email.is_unread ? 'border-blue-500/30' : 'border-slate-700'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                {email.is_unread && !email.parsed && (
                                  <span className="w-2 h-2 rounded-full bg-blue-400" />
                                )}
                                {email.parsed && (
                                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">Parsed</span>
                                )}
                                <span className="text-slate-400 text-xs truncate">{email.from}</span>
                              </div>
                              <p className="text-white font-medium truncate">{email.subject}</p>
                              <p className="text-slate-500 text-xs mt-1">{email.date}</p>
                            </div>
                            {!email.parsed && (
                              <Button
                                size="sm"
                                onClick={() => handleParseGmailEmail(email.gmail_id)}
                                className="bg-gold hover:bg-gold-light text-slate-950"
                                data-testid={`parse-email-${email.gmail_id}`}
                              >
                                <Zap className="w-3 h-3 mr-1" />
                                Parse
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-slate-400">
                  Connect your Gmail account to automatically fetch and parse lender program emails.
                </p>
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <h3 className="text-white font-medium mb-2">How it works:</h3>
                  <ol className="text-slate-400 text-sm space-y-2 list-decimal list-inside">
                    <li>Click "Connect Gmail" to authorize access to your inbox</li>
                    <li>Filter emails by sender or subject (e.g., "rate sheet", "program")</li>
                    <li>Click "Fetch Emails" to retrieve matching emails</li>
                    <li>Parse individual emails or enable auto-parse to extract all programs</li>
                  </ol>
                </div>
                <Button
                  onClick={handleConnectGmail}
                  disabled={isConnectingGmail}
                  className="w-full bg-gold hover:bg-gold-light text-slate-950 font-semibold py-4"
                  data-testid="connect-gmail-btn"
                >
                  {isConnectingGmail ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Link2 className="w-5 h-5 mr-2" />
                      Connect Gmail Account
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Parse Email Tab */}
      {activeTab === 'parse' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Email Input */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-gold" />
              Paste Lender Email
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Email Subject (optional)</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="e.g., New FHA Program - 100% Financing"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder:text-slate-500 focus:border-gold focus:ring-1 focus:ring-gold outline-none"
                  data-testid="email-subject-input"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Sender Email (optional)</label>
                <input
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  placeholder="e.g., rep@lender.com"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder:text-slate-500 focus:border-gold focus:ring-1 focus:ring-gold outline-none"
                  data-testid="sender-email-input"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Email Content *</label>
                <textarea
                  value={emailContent}
                  onChange={(e) => setEmailContent(e.target.value)}
                  placeholder="Paste the full email content here... The AI will extract lender name, program details, FICO requirements, LTV limits, loan types, and more."
                  rows={12}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:border-gold focus:ring-1 focus:ring-gold outline-none resize-none"
                  data-testid="email-content-input"
                />
              </div>
              
              <Button
                onClick={handleParseEmail}
                disabled={isParsingEmail || !emailContent.trim()}
                className="w-full bg-gold hover:bg-gold-light text-slate-950 font-semibold py-3"
                data-testid="parse-email-btn"
              >
                {isParsingEmail ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Extracting Programs...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Extract Programs with AI
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Find Matches */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-green-400" />
              Find Matches for Lead
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Select a Lead</label>
                <select
                  value={selectedLeadId}
                  onChange={(e) => setSelectedLeadId(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-gold focus:ring-1 focus:ring-gold outline-none"
                  data-testid="lead-select"
                >
                  <option value="">-- Select a lead --</option>
                  {leads.filter(l => !['Closed', 'Archived', 'Not Interested'].includes(l.lead_status)).map(lead => (
                    <option key={lead.lead_id} value={lead.lead_id}>
                      {lead.first_name} {lead.last_name} - {lead.reason_for_capital || 'No reason specified'}
                    </option>
                  ))}
                </select>
              </div>
              
              <Button
                onClick={() => handleFindMatches(selectedLeadId)}
                disabled={isFindingMatches || !selectedLeadId}
                className="w-full bg-green-600 hover:bg-green-500 text-white font-semibold py-3"
                data-testid="find-matches-btn"
              >
                {isFindingMatches ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Finding Matches...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Find Matching Programs
                  </>
                )}
              </Button>
              
              <div className="border-t border-slate-700 pt-4 mt-4">
                <p className="text-sm text-slate-400 mb-3">
                  The AI will analyze the lead's requirements (FICO, loan amount, property type, etc.) 
                  and match them against all extracted lender programs.
                </p>
                
                <div className="bg-slate-900/50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-slate-300">FICO Score Compatibility</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-slate-300">Loan Amount Range</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-slate-300">Property Type Match</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-slate-300">Loan Type Match</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-slate-300">LTV Requirements</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Programs Tab */}
      {activeTab === 'programs' && (
        <div className="space-y-4">
          {isLoadingPrograms ? (
            <div className="text-center py-12 text-slate-400">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
              Loading programs...
            </div>
          ) : programs.length === 0 ? (
            <div className="text-center py-12 bg-slate-800/50 border border-slate-700 rounded-xl">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No programs extracted yet</p>
              <p className="text-sm text-slate-500 mt-1">Paste a lender email to extract programs</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {programs.map(program => (
                <div 
                  key={program.program_id}
                  className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden hover:border-slate-600 transition-colors"
                >
                  <div 
                    className="p-4 cursor-pointer"
                    onClick={() => setExpandedProgram(expandedProgram === program.program_id ? null : program.program_id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-gold" />
                          <h3 className="font-semibold text-white">{program.lender_name}</h3>
                        </div>
                        <p className="text-slate-300 mt-1">{program.program_name}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {program.loan_types?.map((type, i) => (
                            <span key={i} className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDeleteProgram(program.program_id); }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`delete-program-${program.program_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        {expandedProgram === program.program_id ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                    
                    {/* Quick Stats */}
                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-400">
                      {program.min_fico && (
                        <span>Min FICO: <strong className="text-white">{program.min_fico}</strong></span>
                      )}
                      {program.max_ltv && (
                        <span>Max LTV: <strong className="text-white">{program.max_ltv}%</strong></span>
                      )}
                      {program.interest_rate_range && (
                        <span>Rate: <strong className="text-white">{program.interest_rate_range}</strong></span>
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedProgram === program.program_id && (
                    <div className="border-t border-slate-700 p-4 bg-slate-900/30">
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500 mb-1">Loan Amount Range</p>
                          <p className="text-white">
                            ${program.min_loan_amount?.toLocaleString() || 'N/A'} - ${program.max_loan_amount?.toLocaleString() || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500 mb-1">Property Types</p>
                          <p className="text-white">{program.property_types?.join(', ') || 'Not specified'}</p>
                        </div>
                        {program.special_features?.length > 0 && (
                          <div className="md:col-span-2">
                            <p className="text-slate-500 mb-1">Special Features</p>
                            <div className="flex flex-wrap gap-2">
                              {program.special_features.map((feature, i) => (
                                <span key={i} className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded">
                                  {feature}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {program.restrictions?.length > 0 && (
                          <div className="md:col-span-2">
                            <p className="text-slate-500 mb-1">Restrictions</p>
                            <div className="flex flex-wrap gap-2">
                              {program.restrictions.map((restriction, i) => (
                                <span key={i} className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded">
                                  {restriction}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {program.contact_info && (
                          <div className="md:col-span-2">
                            <p className="text-slate-500 mb-1">Contact</p>
                            <p className="text-white">{program.contact_info}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Matches Tab */}
      {activeTab === 'matches' && (
        <div className="space-y-4">
          {isLoadingMatches ? (
            <div className="text-center py-12 text-slate-400">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
              Loading matches...
            </div>
          ) : matches.length === 0 ? (
            <div className="text-center py-12 bg-slate-800/50 border border-slate-700 rounded-xl">
              <Target className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No matches found yet</p>
              <p className="text-sm text-slate-500 mt-1">Extract programs and run matching for your leads</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {matches.map(match => (
                <div 
                  key={match.match_id}
                  className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 hover:border-slate-600 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className={`px-3 py-1 rounded-full font-bold text-sm ${getScoreColor(match.match_score)}`}>
                          {match.match_score}%
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">{match.lead_name}</h3>
                          <p className="text-sm text-slate-400">{match.lead_email}</p>
                        </div>
                      </div>
                      
                      <div className="mt-3 pl-12">
                        <p className="text-slate-300">
                          <span className="text-slate-500">Matched with:</span>{' '}
                          <strong>{match.lender_name}</strong> - {match.program_name}
                        </p>
                        
                        {match.match_reasons?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {match.match_reasons.slice(0, 3).map((reason, i) => (
                              <span key={i} className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded">
                                {reason}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(match.status)}`}>
                        {match.status}
                      </span>
                      
                      <div className="flex gap-1">
                        {match.status === 'new' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUpdateMatchStatus(match.match_id, 'reviewed')}
                              className="text-yellow-400 hover:bg-yellow-500/10"
                            >
                              Mark Reviewed
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUpdateMatchStatus(match.match_id, 'contacted')}
                              className="text-green-400 hover:bg-green-500/10"
                            >
                              Contacted
                            </Button>
                          </>
                        )}
                        {match.status !== 'dismissed' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUpdateMatchStatus(match.match_id, 'dismissed')}
                            className="text-slate-400 hover:bg-slate-700"
                          >
                            Dismiss
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
